import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
} from 'reactstrap';

import {
  SimpleTableWithSorting,
  TableCellProps,
  TableDateFormatHelper,
  generateColumn,
  getRTOriginal,
} from 'app/components/common';
import { YesNoIcons } from 'app/components/icons/IconSets';
import APIService from 'services/api';
import { logError } from 'utils/log';

interface IAppVersion {
  id: number;
  app_name: string;
  major: number;
  minor: number;
  patch: number;
  force_update: boolean | number;
  mobile_app_config: Record<string, unknown> | null;
  created_at?: string;
  updated_at?: string;
}

type AppVersionRow = IAppVersion & { versionCode: number };

interface IAppVersionFormValues {
  app_name: string;
  major: number;
  minor: number;
  patch: number;
  force_update: boolean;
  mobile_app_config: string;
}

const CONFIG_EXAMPLE = `{
  "global_alert": {
    "message": "Trwa przerwa techniczna, zamówienia są chwilowo niedostępne.",
    "title": "Przerwa techniczna",
    "level": "warning",
    "dismissible": false
  }
}`;

const emptyFormValues: IAppVersionFormValues = {
  app_name: '',
  major: 1,
  minor: 0,
  patch: 0,
  force_update: false,
  mobile_app_config: '',
};

const toFormValues = (appVersion: IAppVersion): IAppVersionFormValues => ({
  app_name: appVersion.app_name,
  major: appVersion.major,
  minor: appVersion.minor,
  patch: appVersion.patch,
  force_update: !!appVersion.force_update,
  mobile_app_config: appVersion.mobile_app_config
    ? JSON.stringify(appVersion.mobile_app_config, null, 2)
    : '',
});

const validateConfigJson = (text: string): string | null => {
  if (!text.trim()) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (jsonError) {
    return `Niepoprawny JSON: ${jsonError}`;
  }
  if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return 'Konfiguracja musi być obiektem JSON, np. { "global_alert": { ... } }';
  }
  return null;
};

// Hidden page - reachable only by URL, not present in nav.
const AppVersionsPage = () => {
  const [appVersions, setAppVersions] = useState<IAppVersion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [editedId, setEditedId] = useState<number | 'new' | null>(null);
  const [formValues, setFormValues] =
    useState<IAppVersionFormValues>(emptyFormValues);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadAppVersions = useCallback(() => {
    setIsLoading(true);
    APIService.get('/admin/app-status')
      .then((data: IAppVersion[]) => {
        setAppVersions(data);
        setError(null);
      })
      .catch((e) => {
        logError('AppVersionsPage: failed to load app versions', e);
        setError('Nie udało się pobrać listy wersji aplikacji.');
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    loadAppVersions();
  }, [loadAppVersions]);

  const startCreate = () => {
    setEditedId('new');
    setFormValues(emptyFormValues);
    setJsonError(null);
    setSuccessMessage(null);
    setError(null);
  };

  const startEdit = useCallback((appVersion: IAppVersion) => {
    setEditedId(appVersion.id);
    setFormValues(toFormValues(appVersion));
    setJsonError(null);
    setSuccessMessage(null);
    setError(null);
  }, []);

  const closeForm = () => {
    setEditedId(null);
    setFormValues(emptyFormValues);
    setJsonError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const configText = formValues.mobile_app_config.trim();
    const configError = validateConfigJson(configText);
    if (configError) {
      setJsonError(configError);
      return;
    }

    const payload = {
      app_name: formValues.app_name,
      major: formValues.major,
      minor: formValues.minor,
      patch: formValues.patch,
      force_update: formValues.force_update,
      mobile_app_config: configText ? JSON.parse(configText) : null,
    };

    setIsSaving(true);
    setError(null);
    const request =
      editedId === 'new'
        ? APIService.post('/admin/app-status', payload)
        : APIService.put(`/app-status/${editedId}`, payload);

    request
      .then(() => {
        setSuccessMessage('Zapisano.');
        closeForm();
        loadAppVersions();
      })
      .catch((e) => {
        logError('AppVersionsPage: failed to save app version', e);
        setError('Nie udało się zapisać. Sprawdź dane i spróbuj ponownie.');
      })
      .finally(() => setIsSaving(false));
  };

  const handleDelete = useCallback(
    (appVersion: IAppVersion) => {
      const confirmed = window.confirm(
        `Na pewno usunąć wpis #${appVersion.id} (${appVersion.app_name} ${appVersion.major}.${appVersion.minor}.${appVersion.patch})?`
      );
      if (!confirmed) {
        return;
      }
      APIService.del(`/admin/app-status/${appVersion.id}`)
        .then(() => {
          setSuccessMessage('Usunięto.');
          setEditedId((current) =>
            current === appVersion.id ? null : current
          );
          loadAppVersions();
        })
        .catch((e) => {
          logError('AppVersionsPage: failed to delete app version', e);
          setError('Nie udało się usunąć wpisu.');
        });
    },
    [loadAppVersions]
  );

  const updateFormValue = (
    key: keyof IAppVersionFormValues,
    value: string | number | boolean
  ) => setFormValues((prev) => ({ ...prev, [key]: value }));

  const handleConfigChange = (value: string) => {
    updateFormValue('mobile_app_config', value);
    setJsonError(validateConfigJson(value));
  };

  const rows = useMemo<AppVersionRow[]>(
    () =>
      appVersions.map((appVersion) => ({
        ...appVersion,
        versionCode:
          appVersion.major * 1_000_000 +
          appVersion.minor * 1_000 +
          appVersion.patch,
      })),
    [appVersions]
  );

  const columns = useMemo(
    () => [
      generateColumn('id', {
        customHeader: 'ID',
        filterType: 'none',
      }),
      generateColumn('app_name', {
        customHeader: 'Aplikacja',
        filterType: 'select',
      }),
      generateColumn('versionCode', {
        customHeader: 'Min. wersja',
        filterType: 'none',
        customCellFormatter: (props: TableCellProps<AppVersionRow>) => {
          const { major, minor, patch } = getRTOriginal<AppVersionRow>(props);
          return `${major}.${minor}.${patch}`;
        },
      }),
      generateColumn('force_update', {
        customHeader: 'Wymuś aktualizację',
        filterType: 'none',
        customCellFormatter: (props: TableCellProps<AppVersionRow>) => {
          const appVersion = getRTOriginal<AppVersionRow>(props);
          return appVersion.force_update ? YesNoIcons.YES : YesNoIcons.NO;
        },
      }),
      generateColumn('mobile_app_config', {
        customHeader: 'Konfiguracja',
        filterType: 'none',
        customCellFormatter: (props: TableCellProps<AppVersionRow>) => {
          const appVersion = getRTOriginal<AppVersionRow>(props);
          return appVersion.mobile_app_config
            ? Object.keys(appVersion.mobile_app_config).join(', ')
            : '—';
        },
      }),
      generateColumn('updated_at', {
        customHeader: 'Aktualizacja',
        filterType: 'none',
        customCellFormatter: TableDateFormatHelper<AppVersionRow>(
          'updated_at',
          'HH:mm dd.MM.yyyy'
        ),
      }),
      generateColumn('actions', {
        customHeader: '',
        filterType: 'none',
        customCellFormatter: (props: TableCellProps<AppVersionRow>) => {
          const appVersion = getRTOriginal<AppVersionRow>(props);
          return (
            <div className="text-nowrap">
              <Button
                size="sm"
                color="secondary"
                className="mr-2"
                onClick={() => startEdit(appVersion)}
              >
                Edytuj
              </Button>
              <Button
                size="sm"
                color="danger"
                onClick={() => handleDelete(appVersion)}
              >
                Usuń
              </Button>
            </div>
          );
        },
      }),
    ],
    [startEdit, handleDelete]
  );

  return (
    <div className="container-fluid">
      <h2>Wersje i konfiguracja aplikacji mobilnych</h2>
      {error && <Alert color="danger">{error}</Alert>}
      {successMessage && <Alert color="success">{successMessage}</Alert>}
      <Button color="primary" className="mb-3" onClick={startCreate}>
        Dodaj wpis
      </Button>
      {isLoading ? (
        <div>Wczytywanie...</div>
      ) : (
        <SimpleTableWithSorting
          data={rows}
          columns={columns}
          hasFilters
          pageSize={50}
        />
      )}
      {editedId !== null && (
        <Card className="mb-3">
          <CardHeader>
            {editedId === 'new' ? 'Nowy wpis' : `Edycja wpisu #${editedId}`}
          </CardHeader>
          <CardBody>
            <Form onSubmit={handleSubmit}>
              <FormGroup>
                <Label for="app-version-app-name">
                  Nazwa aplikacji (app_name)
                </Label>
                <Input
                  id="app-version-app-name"
                  value={formValues.app_name}
                  placeholder="np. lesznoje_mobile_app"
                  required
                  onChange={(e) => updateFormValue('app_name', e.target.value)}
                />
              </FormGroup>
              <div className="d-flex">
                {(['major', 'minor', 'patch'] as const).map((field) => (
                  <FormGroup key={field} className="mr-3">
                    <Label for={`app-version-${field}`}>{field}</Label>
                    <Input
                      id={`app-version-${field}`}
                      type="number"
                      min={0}
                      value={formValues[field]}
                      required
                      onChange={(e) =>
                        updateFormValue(field, Number(e.target.value))
                      }
                    />
                  </FormGroup>
                ))}
              </div>
              <FormGroup check className="mb-3">
                <Label check>
                  <Input
                    type="checkbox"
                    checked={formValues.force_update}
                    onChange={(e) =>
                      updateFormValue('force_update', e.target.checked)
                    }
                  />{' '}
                  Wymuś aktualizację (force_update)
                </Label>
              </FormGroup>
              <FormGroup>
                <Label for="app-version-config">
                  mobile_app_config (JSON, puste = brak konfiguracji)
                </Label>
                <Input
                  id="app-version-config"
                  type="textarea"
                  rows={12}
                  className="text-monospace"
                  invalid={!!jsonError}
                  value={formValues.mobile_app_config}
                  onChange={(e) => handleConfigChange(e.target.value)}
                />
                {jsonError && <FormFeedback>{jsonError}</FormFeedback>}
                <small className="text-muted">
                  Przykład globalnego alertu (np. przerwa techniczna):
                  <pre className="mt-1 mb-0">{CONFIG_EXAMPLE}</pre>
                </small>
              </FormGroup>
              <Button
                color="primary"
                type="submit"
                disabled={isSaving || !!jsonError}
              >
                {isSaving ? 'Zapisywanie...' : 'Zapisz'}
              </Button>
              <Button
                color="link"
                type="button"
                className="ml-2"
                onClick={closeForm}
              >
                Anuluj
              </Button>
            </Form>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

AppVersionsPage.url = '/admin/app-versions';
AppVersionsPage.navName = 'admin.app-versions';

export default AppVersionsPage;
